import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'chartAbstractionPendingList';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface PendingList {
  measureIds: string[];  // Zero-padded measure IDs: ["001", "047", "128"]
  practiceId: string;    // To validate list matches current practice
}

interface QuestionsDocument {
  id: number;
  questionsData: Record<string, string[]>;
  originalFilename: string;
  measureCount: number;
  maxQuestions: number;
  uploadedAt: string;
}

interface ChartAbstractionContextType {
  // Pending list state
  pendingMeasures: string[];
  pendingCount: number;
  
  // Questions document state
  questionsDocument: QuestionsDocument | null;
  questionsLoading: boolean;
  hasQuestionsDocument: boolean;
  
  // Actions
  addMeasure: (measureId: string) => void;
  removeMeasure: (measureId: string) => void;
  toggleMeasure: (measureId: string) => void;
  clearPendingList: () => void;
  isMeasureInList: (measureId: string) => boolean;
  hasMeasureQuestions: (measureId: string) => boolean;
  getMeasureQuestions: (measureId: string) => string[];
  
  // Refresh questions document
  refreshQuestionsDocument: () => Promise<void>;
}

const ChartAbstractionContext = createContext<ChartAbstractionContextType | undefined>(undefined);

interface ChartAbstractionProviderProps {
  children: ReactNode;
}

export function ChartAbstractionProvider({ children }: ChartAbstractionProviderProps) {
  const { token, selectedPractice } = useAuth();
  const practiceId = selectedPractice?.id?.toString() || '';
  
  // State
  const [pendingMeasures, setPendingMeasures] = useState<string[]>([]);
  const [questionsDocument, setQuestionsDocument] = useState<QuestionsDocument | null>(null);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  // Load pending list from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: PendingList = JSON.parse(stored);
        // Only restore if same practice
        if (parsed.practiceId === practiceId && Array.isArray(parsed.measureIds)) {
          setPendingMeasures(parsed.measureIds);
        } else {
          // Different practice, clear the list
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('[ChartAbstraction] Error loading pending list:', error);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [practiceId]);

  // Save pending list to sessionStorage whenever it changes
  useEffect(() => {
    if (practiceId) {
      try {
        const data: PendingList = {
          measureIds: pendingMeasures,
          practiceId,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.error('[ChartAbstraction] Error saving pending list:', error);
      }
    }
  }, [pendingMeasures, practiceId]);

  // Load questions document on mount
  useEffect(() => {
    if (token) {
      loadQuestionsDocument();
    }
  }, [token]);

  const loadQuestionsDocument = async () => {
    setQuestionsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/chart-abstraction-questions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuestionsDocument(data.exists ? data.document : null);
      }
    } catch (error) {
      console.error('[ChartAbstraction] Error loading questions document:', error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Normalize measure ID to zero-padded format
  const normalizeMeasureId = (measureId: string): string => {
    // Remove any "M" prefix and leading zeros, then re-pad to 3 digits
    const numeric = measureId.replace(/^M/i, '').replace(/^0+/, '') || '0';
    return numeric.padStart(3, '0');
  };

  // Add a measure to the pending list
  const addMeasure = useCallback((measureId: string) => {
    const normalized = normalizeMeasureId(measureId);
    setPendingMeasures(prev => {
      if (prev.includes(normalized)) return prev;
      return [...prev, normalized].sort();
    });
  }, []);

  // Remove a measure from the pending list
  const removeMeasure = useCallback((measureId: string) => {
    const normalized = normalizeMeasureId(measureId);
    setPendingMeasures(prev => prev.filter(id => id !== normalized));
  }, []);

  // Toggle a measure in/out of the pending list
  const toggleMeasure = useCallback((measureId: string) => {
    const normalized = normalizeMeasureId(measureId);
    setPendingMeasures(prev => {
      if (prev.includes(normalized)) {
        return prev.filter(id => id !== normalized);
      }
      return [...prev, normalized].sort();
    });
  }, []);

  // Clear the entire pending list
  const clearPendingList = useCallback(() => {
    setPendingMeasures([]);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  // Check if a measure is in the pending list
  const isMeasureInList = useCallback((measureId: string): boolean => {
    const normalized = normalizeMeasureId(measureId);
    return pendingMeasures.includes(normalized);
  }, [pendingMeasures]);

  // Check if a measure has questions defined
  const hasMeasureQuestions = useCallback((measureId: string): boolean => {
    if (!questionsDocument?.questionsData) return false;
    const normalized = normalizeMeasureId(measureId);
    return normalized in questionsDocument.questionsData && 
           questionsDocument.questionsData[normalized].length > 0;
  }, [questionsDocument]);

  // Get questions for a measure
  const getMeasureQuestions = useCallback((measureId: string): string[] => {
    if (!questionsDocument?.questionsData) return [];
    const normalized = normalizeMeasureId(measureId);
    return questionsDocument.questionsData[normalized] || [];
  }, [questionsDocument]);

  // Refresh questions document
  const refreshQuestionsDocument = useCallback(async () => {
    await loadQuestionsDocument();
  }, [token]);

  const value: ChartAbstractionContextType = {
    pendingMeasures,
    pendingCount: pendingMeasures.length,
    questionsDocument,
    questionsLoading,
    hasQuestionsDocument: !!questionsDocument,
    addMeasure,
    removeMeasure,
    toggleMeasure,
    clearPendingList,
    isMeasureInList,
    hasMeasureQuestions,
    getMeasureQuestions,
    refreshQuestionsDocument,
  };

  return (
    <ChartAbstractionContext.Provider value={value}>
      {children}
    </ChartAbstractionContext.Provider>
  );
}

export function useChartAbstraction() {
  const context = useContext(ChartAbstractionContext);
  if (context === undefined) {
    throw new Error('useChartAbstraction must be used within a ChartAbstractionProvider');
  }
  return context;
}





